import React, { useEffect, useRef, useState } from "react";
import { SanityImage } from "src/interfaces/sanity";
import { StyledSvg } from "../Common/ImageFluid/styles";
import { size } from "src/components/Carousel";
import {
    StyledContainer,
    ContainerLinkSlider,
    StyledImg,
    ImageContainer,
    CarouselContainer,
    StyledSlider,
    Content,
    UpperTextContainer,
    Title,
    Description,
    ProductTitle,
    ProductPrice,
    OkendoReviews,
    StyledButton,
    StyledProduct,
    StyledProductRating,
    MainImageContainer,
    NewProductMessage,
    RatingsBox,
    RatingParagraph,
    StyledRating,
    NewProductMessageContainer,
    BtnBox,
    ComparePrice, 
    Price, 
    ProductSubtitle,
    Label,
    ContentWrapper,
    StyledButtons,
    StyledButtonSecond,
} from "./style";
import trackKlavyioAddedToCart from 'src/utils/mapKlaviyoPayload';
import { eventData } from 'src/utils/dataLayer';
import { useTheme } from "styled-components";
import locales from 'src/locales';
import {
    client,
    useAddItemToCart,
    useUpsells,
    useCartDrawer,
    useStore,
    usePillowcaseMaterialDrawer,
    useCheckout,
    addToCartRedirectToCheckout,
    useCart,
} from 'src/context/siteContext';
import Button from "../Common/Button";
import PageLink from "../Link";
import ArrowRight from "src/images/rightArrow.svg";

interface Props {
    data: {
        title?: string;
        description?: string;
        products?: [
            {
                image: SanityImage;
                product: any;
                labelText?: string, 
                labelBgColor?: object, 
                labelTextColor?: object;
                listOfCtas?: [
                    {
                        enabled?: boolean;
                        link?: string;
                        text?: string;
                    }
                ];
            }
        ];
        saleProducts?: [
            {
                image: SanityImage;
                product: any;
                labelText?: string, 
                labelBgColor?: object, 
                labelTextColor?: object;
                listOfCtas?: [
                    {
                        enabled?: boolean;
                        link?: string;
                        text?: string;
                    }
                ];
            }
        ];
    }
    onClick?: () => void;
}

export const BestSellingProductsSlider = ({ data, onClick }: Props) => {
    const { title, description, products, saleProducts } = data
    const sliderRef1 = useRef(null);
    const sliderRef2 = useRef(null);
    const { checkout } = useCart();
    const theme = useTheme();

    const settings = (ref: any) => ({
        dots: false,
        arrows: true,
        speed: 500,
        infinite: false,
        adaptiveHeight: true,
        slidesToScroll: 1,
        slidesToShow: 4,
        responsive: [
            {
                breakpoint: 1267,
                settings: {
                    slidesToShow: 3,
                }
            },
            {
                breakpoint: 885,
                settings: {
                    slidesToShow: 2,

                }
            },
            {
                breakpoint: 768,
                settings: {
                    slidesToShow: 2,

                }
            },
            {
                breakpoint: 655,
                settings: {
                    slidesToShow: 1,
                }
            },
        ],
        onReInit: () => normalizeHeights(ref),
    });

    const symbol = process.env.GATSBY_CURRENCY_SYMBOL;

    const normalizeHeights = (sliderRef: any) => {
        if (!sliderRef.current) return;

        const track = sliderRef.current.innerSlider.list.querySelector(".slick-track");
        const slides = sliderRef.current.innerSlider.list.querySelectorAll(".slick-slide");

        if (!track || !slides.length) return;

        let maxHeight = 0;
        let contentMaxHeight = 0;

        slides.forEach((slide: any, index: any) => {
            const h: any = Array.from(slide.querySelector('.best-selling-container').children)?.reduce((acc: any, element: any) => acc += element.offsetHeight, 0);
            const contentHeight = 50 + Array.from(
                slide.querySelectorAll('.product-card-content .product-card-content-wrapper')
            )
            .map(wrapper =>
                Array.from((wrapper as HTMLElement).children).reduce((acc, child) =>
                    acc + (child as HTMLElement).offsetHeight
                , 0)
            )
            .reduce((acc, height) => acc + height, 0);

            if (h > maxHeight) maxHeight = h;
            if (contentHeight > contentMaxHeight) contentMaxHeight = contentHeight;
        });

        slides.forEach((slide: any) => {
            slide.style.height = `${maxHeight}px`;
            slide.querySelector('[class*="Content-"]').style.height = `${contentMaxHeight}px`;
        });

        track.style.height = `${maxHeight}px`;
    };

    useEffect(() => {
        const resizeHandler = () => {
            normalizeHeights(sliderRef1);
            normalizeHeights(sliderRef2);
        };

        resizeHandler();
        window.addEventListener("resize", resizeHandler);
        return () => window.removeEventListener("resize", resizeHandler);
    }, []);

    const productsHaveLabels = products?.some(p => !!p?.labelText);
    const saleProductsHaveLabels = saleProducts?.some(p => !!p?.labelText);

    return (
        <StyledContainer>
            <UpperTextContainer>
                <Title>{title}</Title>
                <Description>{description}</Description>
            </UpperTextContainer>
            <CarouselContainer id="bestSelling">
                {products && (
                    <StyledSlider ref={sliderRef1} {...settings(sliderRef1)}>
                        {products.map((prod, i) => {

                            const price = prod?.product?.content?.shopify?.defaultPrice;

                            const shopifyProductId = prod?.product?.content?.shopify?.productId;
                            let message = locales.productInfo.new_product_message;
                            if (shopifyProductId == 6852326654038 || shopifyProductId == 6852243685462) {
                                message = locales.productInfo.moreStock;
                            }        

                            return (
                                <ContainerLinkSlider
                                    to={`/products/${prod.product.content.main.slug.current}`}
                                    className="best-selling-container"
                                    hasLabel={productsHaveLabels}
                                >
                                    {prod?.labelText && <Label background={prod?.labelBgColor} color={prod?.labelTextColor}>{prod?.labelText}</Label>}
                                    <MainImageContainer>
                                        <StyledImg imageRef={prod.image ?? {asset: prod.product.asset}} alt={prod?.image?.alt}/>
                                    </MainImageContainer>
                                    <Content className="product-card-content">
                                        <ContentWrapper justify="start" className="product-card-content-wrapper">
                                            <ProductTitle className="product-title">{prod.product.content.main.pdpTitle}</ProductTitle>
                                            {
                                                prod.product.content.main.newProductMessage
                                                    ? <NewProductMessageContainer className="out-of-stock"><NewProductMessage className="out-of-stock-message">{message}</NewProductMessage></NewProductMessageContainer>
                                                    : <RatingsBox id="rating-box">
                                                        <StyledRating number={prod?.product?.content?.main?.okendoReviews?.rating} color={theme.colors.yellow} />
                                                        <RatingParagraph>
                                                            <span>{prod?.product?.content?.main?.okendoReviews?.totalReviews} Reviews</span>
                                                        </RatingParagraph>
                                                    </RatingsBox>
                                            }
                                            <ProductSubtitle className="product-subtitle">
                                                {prod?.description}
                                            </ProductSubtitle>

                                            {!prod?.listOfCtas && (
                                                <ProductPrice id="productPrice">{symbol + price}</ProductPrice>
                                            )}
                                            <StyledProduct
                                                id={prod.product.content.shopify?.productId}
                                                key={prod.product._id}
                                                addToCart={true}
                                                price={prod?.product?.content?.shopify?.defaultPrice}
                                                title={prod?.product?.content?.main?.title}
                                                productQuantity={prod?.product?.content?.main?.productQuantity?.[0]}
                                            />
                                        </ContentWrapper>
                                        <ContentWrapper align="center" justify="end" className="product-card-content-wrapper">
                                            {prod?.listOfCtas && (
                                                <>
                                                    <ProductPrice id="productPrice">{symbol + price}</ProductPrice>
                                                    <StyledButtons>
                                                        {prod?.listOfCtas?.filter(button => true)?.map(button => (
                                                            <StyledButtonSecond to={button?.link} className="imageRowButton">
                                                                {button?.text}
                                                                <img src={ArrowRight} alt="ArrowRight" />
                                                            </StyledButtonSecond>
                                                        ))}
                                                    </StyledButtons>
                                                </>
                                            )}
                                        </ContentWrapper>
                                    </Content>
                                </ContainerLinkSlider>
                            )
                        })}
                    </StyledSlider>)}
            </CarouselContainer>

            <CarouselContainer id="saleProducts">
                {saleProducts && (
                    <StyledSlider ref={sliderRef2} {...settings(sliderRef2)}>
                        {saleProducts.map((prod, i) => {

                            const price = prod?.product?.content?.shopify?.defaultPrice;

                            const shopifyProductId = prod?.product?.content?.shopify?.productId;
                            let message = locales.productInfo.new_product_message;
                            if (shopifyProductId == 6852326654038 || shopifyProductId == 6852243685462) {
                                message = locales.productInfo.moreStock;
                            }        

                            return (
                                <ContainerLinkSlider
                                    to={`/products/${prod.product.content.main.slug.current}`}
                                    className="best-selling-container"
                                    hasLabel={saleProductsHaveLabels}
                                >
                                    {prod?.labelText && <Label background={prod?.labelBgColor} color={prod?.labelTextColor}>{prod?.labelText}</Label>}
                                    <MainImageContainer>
                                        <StyledImg imageRef={prod.image ?? {asset: prod.product.asset}} alt={prod?.image?.alt}/>
                                    </MainImageContainer>
                                    <Content className="product-card-content">
                                        <ContentWrapper justify="start" className="product-card-content-wrapper">
                                            <ProductTitle className="product-title">{prod.product.content.main.pdpTitle}</ProductTitle>
                                            {
                                                prod.product.content.main.newProductMessage
                                                    ? <NewProductMessageContainer className="out-of-stock"><NewProductMessage className="out-of-stock-message">{message}</NewProductMessage></NewProductMessageContainer>
                                                    : <RatingsBox id="rating-box">
                                                        <StyledRating number={prod?.product?.content?.main?.okendoReviews?.rating} color={theme.colors.yellow} />
                                                        <RatingParagraph>
                                                            <span>{prod?.product?.content?.main?.okendoReviews?.totalReviews} Reviews</span>
                                                        </RatingParagraph>
                                                    </RatingsBox>
                                            }
                                            <ProductSubtitle className="product-subtitle">
                                                {prod?.description}
                                            </ProductSubtitle>

                                            {!prod?.listOfCtas && (
                                                    <ProductPrice id="productPrice">{symbol + price}</ProductPrice>
                                            )}
                                            <StyledProduct
                                                id={prod.product.content.shopify?.productId}
                                                key={prod.product._id}
                                                addToCart={true}
                                                price={prod?.product?.content?.shopify?.defaultPrice}
                                                title={prod?.product?.content?.main?.title}
                                                productQuantity={prod?.product?.content?.main?.productQuantity?.[0]}
                                            />
                                        </ContentWrapper>
                                        <ContentWrapper align="center" justify="end" className="product-card-content-wrapper">
                                            {prod?.listOfCtas && (
                                                <>
                                                    <ProductPrice id="productPrice">{symbol + price}</ProductPrice>
                                                    <StyledButtons>
                                                        {prod?.listOfCtas?.filter(button => true)?.map(button => (
                                                            <StyledButtonSecond to={button?.link} className="imageRowButton">
                                                                {button?.text}
                                                                <img src={ArrowRight} alt="ArrowRight" />
                                                            </StyledButtonSecond>
                                                        ))}
                                                    </StyledButtons>
                                                </>

                                            )}
                                        </ContentWrapper>
                                    </Content>
                                </ContainerLinkSlider>
                            )
                        })}
                    </StyledSlider>)}
            </CarouselContainer>

            {data?.button?.enabled &&
                <BtnBox>
                    <StyledButton to={data?.button?.link}>
                        <span className="label">
                            {data?.button?.text}
                        </span>
                    </StyledButton>
                </BtnBox>}
        </StyledContainer>
    )
}